import i18n from '@/utils/i18n';
import moment from 'moment-timezone';
import React from 'react';
import { createStyles } from '@/contexts/theme-context';
import { formatCurrency } from '@/utils/money';
import { formatPercentage } from '@sales-portal/blocs/utils/money';
import { formatSummaryDate } from '@/utils/date';
import {
  PricingEditField,
  PricingInvoiceHistoryEntry,
} from '@sales-portal/domain/pricing/blocs/stores/pricing-edits-store-types';
import { Typography } from '@usepepper/ui/atoms/typography';
import { View } from 'react-native';

interface Props {
  currencyCode: string;
  field: PricingEditField;
  history: PricingInvoiceHistoryEntry[];
}

const formatQuantity = (quantity: number): string =>
  Number.isInteger(quantity) ? quantity.toFixed(0) : quantity.toFixed(1);

const renderPrice = (
  invoice: PricingInvoiceHistoryEntry,
  currencyCode: string,
): string =>
  invoice.priceInMicros === null
    ? '--'
    : formatCurrency(invoice.priceInMicros, currencyCode);

const renderGrossMarginPercentage = (
  invoice: PricingInvoiceHistoryEntry,
): string =>
  invoice.grossMarginPercentage === null
    ? '--'
    : formatPercentage(invoice.grossMarginPercentage, 1);

export const PricingInvoiceHistoryTable: React.FC<Props> = ({
  currencyCode,
  field,
  history,
}) => {
  const [styles, theme] = useStyles();

  const valueLabel =
    field === PricingEditField.Price
      ? i18n.t('pricing.table.invoice_history.price')
      : i18n.t('pricing.table.invoice_history.margin');

  return (
    <View
      style={styles.historySection}
      testID="pricing-invoice-history-popover"
    >
      {history.length === 0 ? (
        <Typography
          color={theme.sk.color.text.secondary}
          testID="pricing-invoice-history-empty-state"
          variant="paragraph-md"
        >
          {i18n.t('pricing.table.invoice_history.empty')}
        </Typography>
      ) : (
        <View style={styles.historyTable}>
          <View style={styles.historyLabelColumn}>
            <Typography
              color={theme.sk.color.text.tertiary}
              variant="paragraph-md"
            >
              {valueLabel}
            </Typography>
            <Typography
              color={theme.sk.color.text.tertiary}
              variant="paragraph-md"
            >
              {i18n.t('pricing.table.invoice_history.sold')}
            </Typography>
            <Typography
              color={theme.sk.color.text.tertiary}
              variant="paragraph-md"
            >
              {i18n.t('pricing.table.invoice_history.date')}
            </Typography>
          </View>
          {history.map((invoice) => (
            <View
              key={`${invoice.invoiceDate}-${invoice.quantity}`}
              style={styles.historyDataColumn}
            >
              <Typography variant="paragraph-md">
                {field === PricingEditField.Price
                  ? renderPrice(invoice, currencyCode)
                  : renderGrossMarginPercentage(invoice)}
              </Typography>
              <Typography variant="paragraph-md">
                {formatQuantity(invoice.quantity)}
              </Typography>
              <Typography variant="paragraph-md">
                {formatSummaryDate(
                  moment.tz(invoice.invoiceDate, moment.tz.guess()),
                )}
              </Typography>
            </View>
          ))}
        </View>
      )}
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  historyDataColumn: {
    alignItems: 'flex-end',
    gap: theme.sk.spacing.spaceSmall,
    minWidth: 64,
  },
  historyLabelColumn: {
    alignItems: 'flex-start',
    gap: theme.sk.spacing.spaceSmall,
  },
  historySection: {
    gap: theme.sk.spacing.spaceSmall,
    padding: theme.sk.spacing.spaceMedium,
  },
  historyTable: {
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceMedium,
  },
}));
